import { action, makeObservable, observable, toJS } from 'mobx';
import { cast, castToReferenceSnapshot, getSnapshot, Instance, SnapshotIn, types } from 'mobx-state-tree';
import { Extent, IExtent } from './extentModel';
import { ILayer, ILayerProps, LayerMobxModel, LayerModel } from './layerModel';

export interface IMapConfigurationProps {
  layersTree: ILayerProps[];
  baseLayer?: string | number;
  layerToConfigure?: string | number;
  extent?: IExtent | null;
  webMapId?: string;
  serviceUrl?: string;
}

export interface IMapConfiguration extends Omit<IMapConfigurationProps, 'baseLayer' | 'layerToConfigure'> {
  layersTree: ILayer[];
  baseLayer?: ILayer;
  layerToConfigure?: ILayer;
  hasChanges: boolean;
  setupWebMap: (webMapId: string, layersToAdd: ILayerProps[]) => void;
  setServiceUrl: (url: string) => void;
  removeWebMap: () => void;
  addLayerToTree: (layer: ILayerProps, removePrevious: boolean) => void;
  removeLayerFromTree: (identifier: string) => void;
  cleanTree: (identifier: string) => void;
  extentAsJSON: () => IExtent | undefined | null;
  loadLayer: (layer: ILayerProps, sublayers: ILayerProps[], index: number) => void;
  addBaseLayer: (newBaseLayer: ILayerProps) => void;
  setLayerToConfigure: (identifier?: string) => void;
  rearrangeLayers: (draggedLayer: string, neighbor: string, dropPosition: number) => void;
  setExtent: (extent: IExtent) => void;
  setHasChanges: (value: boolean) => void;
  getLayerRole: (id: string) => string | undefined;
}

export class MapConfigurationMobxModel implements IMapConfiguration {
  layersTree: LayerMobxModel[] = [];
  baseLayer?: ILayer;
  layerToConfigure?: ILayer;
  extent?: IExtent | null;
  webMapId?: string;
  serviceUrl?: string;
  hasChanges = false;

  constructor(options?: IMapConfigurationProps) {
    if (options?.layersTree?.length) {
      const { layersTree, baseLayer, layerToConfigure, ...rest } = options;
      Object.assign(this, rest);
      layersTree.forEach((layer) => this.layersTree.push(new LayerMobxModel(layer)));
      if (baseLayer) {
        const layer = this.layersTree.find((x) => x.identifier === baseLayer);
        if (layer) {
          this.baseLayer = layer;
        }
      }
      if (layerToConfigure) {
        const layer = this.layersTree.find((x) => x.identifier === layerToConfigure);
        if (layer) {
          this.layerToConfigure = layer;
        }
      }
    }
    makeObservable(this, {
      layersTree: observable,
      baseLayer: observable,
      layerToConfigure: observable,
      extent: observable,
      webMapId: observable,
      serviceUrl: observable,
      hasChanges: observable,
      setupWebMap: action,
      setServiceUrl: action,
      removeWebMap: action,
      addLayerToTree: action,
      removeLayerFromTree: action,
      cleanTree: action,
      loadLayer: action,
      addBaseLayer: action,
      setLayerToConfigure: action,
      rearrangeLayers: action,
      setExtent: action,
      setHasChanges: action
    });
  }

  public setupWebMap(webMapId: string, layersToAdd: ILayerProps[]) {
    this.extent = null;
    this.baseLayer = undefined;
    this.layerToConfigure = undefined;
    this.layersTree = layersToAdd.map((layer) => new LayerMobxModel(layer));
    this.webMapId = webMapId;
    this.setHasChanges(true);
  }
  public setServiceUrl(url: string) {
    this.serviceUrl = url;
    this.setHasChanges(true);
  }
  public removeWebMap() {
    this.extent = null;
    this.baseLayer = undefined;
    this.layerToConfigure = undefined;
    this.layersTree = [];
    this.webMapId = undefined;
    this.setHasChanges(true);
  }
  public addLayerToTree(layer: ILayerProps, removePrevious: boolean) {
    if (removePrevious) {
      const layerToRemove = this.layersTree.find((l) => l.role === layer.role);
      if (layerToRemove && layerToRemove.id) {
        this.removeLayerFromTree(layerToRemove.id.toString());
      }
    }

    this.layersTree.push(new LayerMobxModel(layer));
    this.setHasChanges(true);
  }
  public removeLayerFromTree(identifier: string) {
    const index = this.layersTree.findIndex((x) => x.identifier === identifier);

    if (index !== -1) {
      if (this.layersTree[index].isBaseMap) {
        this.extent = null;
        this.baseLayer = undefined;
        this.layerToConfigure = undefined;
        this.layersTree = [];
      } else {
        this.cleanTree(identifier);
        this.layerToConfigure = undefined;
        this.layersTree.splice(index, 1);
      }
    }
    this.setHasChanges(true);
  }
  public cleanTree(identifier: string) {
    const index = this.layersTree.findIndex((x) => x.identifier === identifier);
    const sublayersToRemoveFromMap = [this.layersTree[index].identifier];

    if (this.layersTree[index].sublayers.length) {
      getIdentifiers(this.layersTree[index].sublayers, sublayersToRemoveFromMap);
    }

    this.setHasChanges(true);
  }
  public loadLayer(layer: ILayerProps, sublayers: ILayerProps[], index: number) {
    const title = this.layersTree[index].title || (layer.title && decodeURIComponent(layer.title));
    this.layersTree[index].title = title;
    this.layersTree[index].id = layer.id;
    this.layersTree[index].opacity = layer.opacity;
    this.layersTree[index].visible = layer.visible;
    this.layersTree[index].sublayers = sublayers.map((subLayer) => new LayerMobxModel(subLayer));
    this.layersTree[index].loaded = true;
  }
  public addBaseLayer(newBaseLayer: ILayerProps) {
    if (this.baseLayer) {
      this.extent = null;

      if (this.baseLayer.wkid !== newBaseLayer.wkid) {
        this.layersTree = [];
        this.layersTree.push(new LayerMobxModel(newBaseLayer));
      } else {
        const index = this.layersTree.findIndex((x) => x.identifier === this.baseLayer?.identifier);
        this.cleanTree(this.baseLayer.identifier);
        this.layersTree[index] = new LayerMobxModel(newBaseLayer);
      }
    } else {
      this.layersTree.push(new LayerMobxModel(newBaseLayer));
    }

    const layer = this.layersTree.find((x) => x.identifier === newBaseLayer.identifier);
    if (layer) {
      this.baseLayer = layer;
    }
    this.setHasChanges(true);
  }
  public setLayerToConfigure(identifier?: string) {
    if (identifier) {
      const layer = this.layersTree.find((x) => x.identifier === identifier);
      if (layer) {
        this.layerToConfigure = layer;
      }
    } else {
      this.layerToConfigure = undefined;
    }
  }
  public rearrangeLayers(draggedLayer: string, neighbor: string, dropPosition: number) {
    const layerToDrag = this.layersTree.find((x) => x.identifier === draggedLayer);
    if (layerToDrag) {
      const layerSnapshot = toJS(layerToDrag);
      this.layersTree.splice(this.layersTree.indexOf(layerToDrag), 1);
      const neighborIndex = this.layersTree.findIndex((x) => x.identifier === neighbor);

      // if dropPosition = -1, then new layer position is below neighbor
      // if dropPosition = 1, then new layer position is above neighbor
      if (dropPosition === 1) {
        this.layersTree.splice(neighborIndex, 0, layerSnapshot);
      } else {
        this.layersTree.splice(neighborIndex + 1, 0, layerSnapshot);
      }
    }
    this.setHasChanges(true);
  }
  public setExtent(extent: IExtent) {
    this.extent = extent;
    this.setHasChanges(true);
  }
  public setHasChanges(value: boolean) {
    this.hasChanges = value;
  }
  public getLayerRole(id: string) {
    const layer = this.layersTree.find((l) => l.id === id);

    return layer && layer.role;
  }
  public extentAsJSON(): IExtent | undefined | null {
    return this.extent ? toJS(this.extent) : undefined;
  }
  public toJSON(): IMapConfigurationProps {
    return {
      ...toJS(this),
      baseLayer: this.baseLayer?.identifier,
      layerToConfigure: this.layerToConfigure?.identifier
    };
  }
}

const getIdentifiers = (sublayers: ILayerProps[], listOfSublayersInMap: string[]) => {
  sublayers.forEach((l) => {
    listOfSublayersInMap.push(l.identifier);

    if (l.sublayers?.length) {
      getIdentifiers(l.sublayers, listOfSublayersInMap);
    }
  });
};

export const MapConfigurationModel = types
  .model('MapConfigurationModel', {
    layersTree: types.array(LayerModel),
    baseLayer: types.maybe(types.safeReference(LayerModel)),
    layerToConfigure: types.maybe(types.reference(LayerModel)),
    extent: types.maybeNull(Extent),
    webMapId: types.maybe(types.string),
    serviceUrl: types.maybe(types.string)
  })
  .volatile(() => ({
    hasChanges: false
  }))
  .actions((self) => ({
    setupWebMap(webMapId: string, layersToAdd: ILayerProps[]) {
      self.extent = null;
      self.baseLayer = undefined;
      self.layerToConfigure = undefined;
      self.layersTree = cast(layersToAdd);
      self.webMapId = webMapId;
      this.setHasChanges(true);
    },
    setServiceUrl(url: string) {
      self.serviceUrl = url;
      this.setHasChanges(true);
    },
    removeWebMap() {
      self.extent = null;
      self.baseLayer = undefined;
      self.layerToConfigure = undefined;
      self.layersTree.clear();
      self.webMapId = undefined;
      this.setHasChanges(true);
    },
    addLayerToTree(layer: ILayerProps, removePrevious: boolean) {
      if (removePrevious) {
        const layerToRemove = self.layersTree.find((l) => l.role === layer.role);
        if (layerToRemove && layerToRemove.id) {
          this.removeLayerFromTree(layerToRemove.id.toString());
        }
      }

      self.layersTree.push(layer);
      this.setHasChanges(true);
    },
    removeLayerFromTree(identifier: string) {
      const index = self.layersTree.findIndex((x) => x.identifier === identifier);

      if (index !== -1) {
        if (self.layersTree[index].isBaseMap) {
          self.extent = null;
          self.baseLayer = undefined;
          self.layerToConfigure = undefined;
          self.layersTree.clear();
        } else {
          this.cleanTree(identifier);
          self.layerToConfigure = undefined;
          self.layersTree.splice(index, 1);
        }
      }
      this.setHasChanges(true);
    },
    cleanTree(identifier: string) {
      const index = self.layersTree.findIndex((x) => x.identifier === identifier);
      const sublayersToRemoveFromMap = [self.layersTree[index].identifier];

      if (self.layersTree[index].sublayers.length) {
        getIdentifiers(self.layersTree[index].sublayers, sublayersToRemoveFromMap);
      }

      this.setHasChanges(true);
    },
    loadLayer(layer: ILayerProps, sublayers: ILayerProps[], index: number) {
      const title = self.layersTree[index].title || (layer.title && decodeURIComponent(layer.title));
      self.layersTree[index].title = title;
      self.layersTree[index].id = layer.id;
      self.layersTree[index].opacity = layer.opacity;
      self.layersTree[index].visible = layer.visible;
      self.layersTree[index].sublayers.replace(sublayers);
      self.layersTree[index].loaded = true;
    },
    addBaseLayer(newBaseLayer: ILayerProps) {
      if (self.baseLayer) {
        self.extent = null;

        if (self.baseLayer.wkid !== newBaseLayer.wkid) {
          self.layersTree.clear();
          self.layersTree.push(newBaseLayer);
        } else {
          const index = self.layersTree.findIndex((x) => x.identifier === self.baseLayer?.identifier);
          this.cleanTree(self.baseLayer.identifier);
          self.layersTree[index] = cast(newBaseLayer);
        }
      } else {
        self.layersTree.push(newBaseLayer);
      }

      const layer = self.layersTree.find((x) => x.identifier === newBaseLayer.identifier);
      if (layer) {
        (self as any).baseLayer = castToReferenceSnapshot(layer);
      }

      this.setHasChanges(true);
    },
    setLayerToConfigure(identifier?: string) {
      if (identifier) {
        const layer = self.layersTree.find((x) => x.identifier === identifier);
        if (layer) {
          self.layerToConfigure = layer;
        }
      } else {
        self.layerToConfigure = undefined;
      }
      this.setHasChanges(true);
    },
    rearrangeLayers(draggedLayer: string, neighbor: string, dropPosition: number) {
      const layerToDrag = self.layersTree.find((x) => x.identifier === draggedLayer);
      if (layerToDrag) {
        const layerSnapshot = getSnapshot(layerToDrag);
        self.layersTree.splice(self.layersTree.indexOf(layerToDrag), 1);
        const neighborIndex = self.layersTree.findIndex((x) => x.identifier === neighbor);

        // if dropPosition = -1, then new layer position is below neighbor
        // if dropPosition = 1, then new layer position is above neighbor
        if (dropPosition === 1) {
          self.layersTree.splice(neighborIndex, 0, layerSnapshot);
        } else {
          self.layersTree.splice(neighborIndex + 1, 0, layerSnapshot);
        }
      }
      this.setHasChanges(true);
    },
    setExtent(extent: IExtent) {
      self.extent = extent;
      this.setHasChanges(true);
    },
    setHasChanges(value: boolean) {
      self.hasChanges = value;
    }
  }))
  .views((self) => ({
    getLayerRole(id: string) {
      const layer = self.layersTree.find((l) => l.id === id);

      return layer && layer.role;
    },
    extentAsJSON(): IExtent | undefined | null {
      return self.extent ? getSnapshot(self.extent) : undefined;
    },
    toJSON(): IMapConfigurationProps {
      return getSnapshot(self);
    }
  }));

export interface IMapConfigurationModelInstance extends Instance<typeof MapConfigurationModel> {}
export interface IMapConfigurationModelSnapshotIn extends SnapshotIn<typeof MapConfigurationModel> {}
